import GolfCartHero from 'assets/images/psp/PSPHero/golf-cart-hero.webp';
import WeatherDamage from 'assets/images/icons/psp/sizechart/WeatherDamage.svg';
import BatteryDeterioration from 'assets/images/icons/psp/sizechart/BatteryDeterioration.svg';
import MaintenanceAccess from 'assets/images/icons/psp/sizechart/MaintenanceAccess.svg';
import SpaceRequirements from 'assets/images/icons/psp/sizechart/SpaceRequirements.svg';
import SunExposure from 'assets/images/icons/psp/sizechart/SunExposure.svg';
import TheftRisk from 'assets/images/icons/psp/sizechart/TheftRisk.svg';

import BasicVehicleStorage from 'assets/images/icons/psp/sizechart/BasicVehicleStorage.svg';
import LargeIndoorUnit from 'assets/images/icons/psp/sizechart/LargeIndoorUnit.svg';
import CoveredVehicleStorage from 'assets/images/icons/psp/sizechart/CoveredVehicleStorage.svg';
import IndoorClimateControlled from 'assets/images/icons/psp/sizechart/IndoorClimateControlled.svg';

import ClimateProtection from 'assets/images/icons/psp/sizechart/ClimateProtection.svg';
import SecurityYouCanTrust from 'assets/images/icons/psp/sizechart/SecurityYouCanTrust.svg';
import ConvenienceFactor from 'assets/images/icons/psp/sizechart/ConvenienceFactor.svg';
import ElectricalAccess from 'assets/images/icons/psp/sizechart/ElectricalAccess.svg';

import Featured1 from 'assets/images/psp/FeaturedContentImages/GolfCart/motorcycle.png';
import Featured2 from 'assets/images/psp/FeaturedContentImages/GolfCart/shutterstock.jpg';
import Featured3 from 'assets/images/psp/FeaturedContentImages/GolfCart/indoor-boat.jpg';
import Featured4 from 'assets/images/psp/FeaturedContentImages/GolfCart/boxesofstuff.webp';

import { storageCategories } from '../utils/getPSPLinks';

const reviews = [
	{
		title: 'Easy-to-use website',
		stars: 5,
		text: 'Easy-to-use website for booking my new storage unit online. Good prices for car storage.',
		customerName: 'CJ',
		date: 'Dec 31, 2024',
		link: 'https://www.trustpilot.com/users/60f62aaa33ff06001154cbe7',
	},
	{
		title: 'Awesomeness',
		stars: 5,
		text: 'I was able to do the entire reservation process while in the passenger seat of a car driving to the storage lot!',
		customerName: 'Robert Ober',
		date: 'Feb 2, 2023',
		link: 'https://www.trustpilot.com/users/63dbe1352a1d7800129f8607',
	},
	{
		title: 'The whole experience went quickly and…',
		stars: 5,
		text: 'The whole experience went quickly and smoothly. The SelfStorage agent was pleasant and efficient. The price was right. The outdoor car lot was well-lit and very secure. I am pleased with my decision to store our vehicle here.',
		customerName: 'Lisa Dahn',
		date: 'Oct 15, 2021',
		link: 'https://www.trustpilot.com/users/5d0a1c654e2d91287ebc737a',
	},
	{
		title: 'Top notch service',
		stars: 5,
		text: 'I was able to reserve and pay online for the parking space to store my car without having to go to their office. Their follow-up phone call and emails made it a very simple and easy-to-follow process.',
		customerName: 'Bill',
		date: 'Nov 17, 2020',
		link: 'https://www.trustpilot.com/reviews/5fb408465e693f0a8429d399',
	},
];
const faqs = [
	{
		question: 'What size storage unit do I need for my golf cart?',
		answer: '[Most standard golf carts fit comfortably in a 10x12 unit](https://www.selfstorage.com/blog/car-storage-size-guide/), which also provides space for accessories like your golf cart rain cover and maintenance supplies.',
	},
	{
		question: "Can I charge my golf cart battery while it's in storage?",
		answer: 'Many facilities offer units with electrical outlets specifically for this purpose. Ask about powered units when making your reservation.',
	},
	{
		question: 'How should I prepare my golf cart for winter storage?',
		answer: 'Clean it thoroughly, charge the battery fully, and consider using a breathable golf cart cover to prevent moisture buildup during golf cart winter storage.',
	},
	{
		question: 'Is climate controlled storage worth the extra cost?',
		answer: 'For golf carts stored more than 3-4 months, climate control prevents battery damage and upholstery issues that could cost hundreds to repair.',
	},
	{
		question: 'Can I access my golf cart anytime during storage?',
		answer: 'Most facilities offer extended access hours, with many [providing 24/7 access](https://www.selfstorage.com/blog/24-hour-storage-amenities/) so you can retrieve your cart whenever you want to hit the course.',
	},
	{
		question: 'Do I need insurance for my stored golf cart?',
		answer: "Check if your homeowner's policy covers off-site storage, or consider adding storage insurance for complete peace of mind.",
	},
	{
		question: "What's not allowed in golf cart storage units?",
		answer: 'Most facilities prohibit hazardous materials like extra fuel, but standard golf cart batteries and accessories are typically fine.',
	},
];
export const GolfCartContent = {
	type: 'Golf Cart',
	reviews: reviews,
	sections: [
		{
			title: 'Golf Cart Storage Hero',
			background: 'default',
			modules: [
				{
					type: 'hero',
					id: 'golf-storage-hero',
					props: {
						content: {
							title: 'Find Affordable Golf Cart Self Storage',
							description:
								'Tired of watching your golf cart weather the elements in your driveway? Golf cart storage units provide the perfect climate controlled environment to protect your cart from UV damage, rain, debris, and risk of theft.\n\nWhether you need [winter storage](https://www.selfstorage.com/blog/moving-in-the-winter/), year-round protection, or just want to free up garage space at home, we can connect you to storage facilities that provide the security and convenience busy homeowners deserve.\n\n **SelfStorage.com** makes it simple to find and reserve the perfect golf cart storage facility',
							image: GolfCartHero,
							CTAtext: 'Find Golf Cart Self Storage',
						},
					},
				},
			],
		},
		{
			title: 'Size Chart',
			modules: [
				{
					title: 'Size Chart ',
					type: 'cardStrip',
					props: {
						heading:
							'The Golf Cart Storage Dilemma Every Owner Faces',
						description: `You invested in a beautiful golf cart to enhance your lifestyle, but now you're facing the reality of where to store it safely. Your driveway leaves it exposed to harsh weather, your garage is already packed with cars and seasonal items, and you're watching your investment slowly deteriorate from the elements.`,
						content:
							"A simple golf cart rain cover isn't enough protection against these risks. Professional golf cart storage offers the complete protection of a golf cart garage without the expense of building one at home.",
						cardData: [
							{
								content:
									'**Sun Exposure**\n\nUV rays fade paint, crack seats, and damage plastic component\n\n→ Covered, shaded units protect from harmful UV radiation',
								imageSrc: SunExposure,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Weather Damage**\n\nRain, snow, and hail cause rust, electrical issues, and upholstery damage\n\n→ Enclosed units provide complete weather protection year-round',
								imageSrc: WeatherDamage,
								contentAlign: 'text-left',
							},
							{
								content:
									"**Space Requirements**\n\nMost home garages can't accommodate a golf cart alongside cars and storage\n\n→ [Right-sized units](https://www.selfstorage.com/blog/what-to-expect) designed specifically for golf cart dimensions",
								imageSrc: SpaceRequirements,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Theft Risk**\n\nUnsecured carts in driveways are easy targets for thieves\n\n→ Gated facilities with 24/7 surveillance and individual unit security',
								imageSrc: TheftRisk,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Battery Deterioration**\n\nExtreme temperatures drain batteries and shorten their lifespan\n\n→ [Climate controlled](https://www.selfstorage.com/blog/what-is-climate-controlled-storage-2/) options maintain optimal battery storage conditions',
								imageSrc: BatteryDeterioration,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Maintenance Access**\n\nNo convenient place to clean, charge, or service your cart\n\n→ [Units with electrical outlets](https://www.selfstorage.com/blog/storage-units-with-electricity/) allow for easy battery maintenance',
								imageSrc: MaintenanceAccess,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},
		{
			title: 'Size Chart2',
			background: 'gray',
			modules: [
				{
					title: 'Size Chart 2',
					type: 'cardStrip',
					props: {
						heading: 'Golf Cart Storage Solutions & Pricing',
						description:
							"You invested in a beautiful golf cart to enhance your lifestyle, but now you're facing the reality of where to store it safely. Your driveway leaves it exposed to harsh weather, your garage is already packed with cars and seasonal items, and you're watching your investment slowly deteriorate from the elements.",
						content:
							'Pricing based on data from **SelfStorage.com**. Vehicle storage costs vary by location, with indoor options ranging from basic outdoor spaces to premium climate controlled units.',
						cardData: [
							{
								title: 'Basic Vehicle Storage',
								content:
									'**Monthly cost**: $45-$119*\n\n**Best for**: Single golf cart, seasonal storage\n\n**Key features**: Basic protection, drive-up access, security',
								imageSrc: BasicVehicleStorage,
								contentAlign: 'text-left',
							},
							{
								title: 'Covered Vehicle Storage',
								content:
									'**Monthly cost**:  $75-$125*\n\n**Best for**: Weather protection, year-round storage\n\n**Key features**: Protection from elements, convenient access',
								imageSrc: CoveredVehicleStorage,
								contentAlign: 'text-left',
							},
							{
								title: 'Indoor Climate-Controlled',
								content:
									'**Monthly cost**:  $100-$150*\n\n**Best for**: Premium carts, battery preservation\n\n**Key features**: Temperature control, humidity protection',
								imageSrc: IndoorClimateControlled,
								contentAlign: 'text-left',
							},
							{
								title: 'Large Indoor Unit (10x20)',
								content:
									'**Monthly cost**:  $140*\n\n**Best for**: Golf cart + accessories, equipment storage\n\n**Key features**: Extra space for maintenance, multiple items',
								imageSrc: LargeIndoorUnit,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},
		{
			title: 'Size Chart2',
			modules: [
				{
					title: 'Storage Unit Size Chart 2',
					type: 'cardStrip',
					props: {
						heading:
							'Why Professional Storage Beats DIY Golf Cart Storage Ideas',
						cardData: [
							{
								title: 'Electrical Access for Battery Maintenance',
								content:
									'Many storage facilities offer units with electrical outlets, perfect for maintaining battery charge during extended storage periods.',
								imageSrc: ElectricalAccess,
								contentAlign: 'text-left',
							},
							{
								title: 'Security You Can Trust',
								content:
									'24/7 surveillance, gated access, and individual unit alarms protect your golf cart better than most home garages.',
								imageSrc: SecurityYouCanTrust,
								contentAlign: 'text-left',
							},
							{
								title: 'Climate Protection',
								content:
									'Professional climate control prevents battery damage and upholstery deterioration that comes with temperature extremes.',
								imageSrc: ClimateProtection,
								contentAlign: 'text-left',
							},
							{
								title: 'Convenience Factor',
								content:
									'No need to figure out golf cart garage size requirements or construction permits – just [rent the space you need](https://www.selfstorage.com/blog/self-storage-unit-size-guide-2/), when you need it.',
								imageSrc: ConvenienceFactor,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},
		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			modules: [
				{
					type: 'reviews',
					props: {
						reviewsData: {
							headerTitle: 'Golf Cart Self Storage: ',
							reviews,
						},
					},
				},
			],
		},
		{
			title: 'Frequently Asked Questions',
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'Golf Cart Self Storage FAQs',
							faqs,
						},
					},
				},
			],
		},
		{
			title: 'featuredContent',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: {
							heading: 'Learn All About Boat Self Storage',
							data: {
								slug: 'golf-related-content',
								unitCards: [
									{
										title: 'Vehicle Storage',
										date: 'October 16, 2025',
										image: Featured1,
										content:
											'Small Vehicle Storage: How to Store Motorcycles, ATVs, and Golf Carts ',
										url: 'https://www.selfstorage.com/blog/small-vehicle-storage-guide/',
									},
									{
										title: 'Storage Tips',
										date: 'October 16, 2025',
										image: Featured2,
										content:
											'Sports Equipment Storage Guide: How to Create A Sports and Outdoor Gear Storage Room',
										url: 'https://www.selfstorage.com/blog/outdoor-gear-shed-storage-unit/',
									},
									{
										title: 'Vehicle Storage',
										date: 'October 10, 2025',
										image: Featured3,
										content:
											'Marina Storage vs. Indoor Storage: Choosing the Right Option for Your Boat',
										url: 'https://www.selfstorage.com/blog/indoor-boat-storage/',
									},
									{
										title: 'Storage Tips',
										date: 'October 16, 2025',
										image: Featured4,
										content:
											'The Ultimate Storage Unit Size Guide: Finding the Perfect Fit for Your Storage Needs  ',
										url: 'https://www.selfstorage.com/blog/self-storage-unit-size-guide-2/',
									},
								],
							},
						},
					},
				},
			],
		},
		{
			title: 'Link to other PSPs',
			background: 'lighterBlue',
			modules: [
				{
					type: 'links',
					props: {
						heading: {
							headingName: 'More ways to store',
						},
						linksList: storageCategories.golfCart,
					},
				},
			],
		},
		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'Golf Cart',
					},
				},
			],
		},
	],
};
